import { useState, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { ArchiveBoxArrowDownIcon, ChevronDownIcon, ChevronUpIcon, InformationCircleIcon, TrashIcon } from '@heroicons/react/24/outline'
import { selectIfUserIsAdmin } from '@/features/auth/authSlice'
import ProcessCard from './ProcessCard'
import MacroProcessEditButton from '@/features/macro-process-edit/components/MacroProcessEditButton'

const PURPOSE_LABELS = {
  location: 'Location',
  vente: 'Vente'
}

const STATUS_LABELS = {
  pending: 'En cours',
  completed: 'Terminé',
  archived: 'Archivé'
}

/**
 * Section MacroProcess : affiche un MacroProcess avec ses ProcessContainers et ProcessTasks
 *
 * @param {Object} macroProcess - Le macro process avec process_containers
 * @param {Object} tasks - Cache { [containerId]: [tasks] }
 * @param {Function} onComplete - Compléter une task
 * @param {Function} onStart - Démarrer une task
 * @param {Function} onValidate - Valider une task (admin)
 * @param {Function} onReopen - Rouvrir une task (admin)
 * @param {Function} onCancel - Annuler le macro process
 * @param {Function} onDelete - Supprimer le macro process (admin)
 */
const MacroProcessSection = ({
  macroProcess,
  tasks,
  expanded = false, 
  onComplete,
  onStart,
  onValidate,
  onReopen,
  onCancel,
  onDelete
}) => {
  const isAdmin = useSelector(selectIfUserIsAdmin)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isActing, setIsActing] = useState(false)

  const { bien, name, process_containers = [], status, purpose } = macroProcess

  const [expandedView, setExpandedView] = useState(expanded)

  const bienLabel = bien ? (bien.codage_interne || bien.adresse_complete) : 'Bien inconnu'
  const statusLabel = STATUS_LABELS[status] || status
  const purposeLabel = PURPOSE_LABELS[purpose] || purpose

  // Tri des process selon leur statut
  // Les process terminés ou annulés sont triés en dernier
  // et seront masqués à l'affichage
  const sortedProcessContainers = useMemo( () => {
    // const statusOrder = { pending: 0, completed: 1, cancelled: 2 }
    // sort of procress_containers : 
    // [...process_containers].sort((a, b) => {
    //   const aOrder = statusOrder[a.status] ?? 999
    //   const bOrder = statusOrder[b.status] ?? 999
    //   if (aOrder !== bOrder) return aOrder - bOrder
    //   return (a.position || 0) - (b.position || 0)
    // })
    return [...process_containers].filter( p => p.status === "pending" )
  }, [process_containers])

  const handleConfirmCancel = async () => {
    setIsActing(true)
    try {
      await onCancel(macroProcess.id)
      setShowCancelModal(false)
    } finally {
      setIsActing(false)
    }
  }

  const handleConfirmDelete = async () => {
    setIsActing(true)
    try {
      await onDelete(macroProcess.id)
      setShowDeleteModal(false)
    } finally {
      setIsActing(false)
    }
  }

  const toggleView = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setExpandedView(!expandedView)
  }

  const getBienURL = () => {
    if(macroProcess.bien) {
      return macroProcess.bien.links?.show
    } 
    return null
  }

  return (
    <div className="border border-base-300">
      {/* Header du MacroProcess */}
      <div className="bg-base-200 px-4 py-3 flex items-center justify-between">
        { getBienURL() && (
            <div className='flex flex-nowrap items-center p-1'>
              <a
                className='btn btn-square btn-ghost btn-xs p-1 hover:bg-primary-400'
                href={getBienURL()}
                title="Aller sur la fiche du bien"
              >
                <InformationCircleIcon/>
              </a>
            </div>
        )}
        <div className="flex flex-1 items-center min-w-0 gap-2 cursor-pointer" onClick={toggleView} >
          <span className="text-base-content text-lg">{bienLabel}</span>
          <span>-</span>
          <span className="font-bold text-base-content text-lg">{name}</span>
          <span>-</span>
          <span>{statusLabel}</span>
        </div>

        {/* Actions */}
        <div className="flex gap-1 flex-shrink-0">
          {status === 'pending' && isAdmin && (
            <MacroProcessEditButton macroProcess={macroProcess} />
          )}
          {status === 'pending' && isAdmin && (
            <>
            <button
              onClick={() => setShowCancelModal(true)}
              disabled={isActing}
              className="btn btn-sm btn-ghost btn-outline hover:bg-error"
              title="Archiver le macro process"
              aria-label="Archiver le macro process"
              
            >
              <ArchiveBoxArrowDownIcon className="h-4 w-4" />
            </button>
            <button
              onClick={() => setShowDeleteModal(true)}
              disabled={isActing}
              className="btn btn-sm btn-ghost btn-outline hover:bg-error"
              title="Supprimer le macro process"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
            </>
          )}
          <button 
            onClick={toggleView}
            className="btn btn-sm btn-ghost btn-square hover:bg-primary-400"
            title={expandedView ? "Masquer les détails" : "Voir les détails" }
          >
            {expandedView ? <ChevronUpIcon className="h-5 w-5" /> : <ChevronDownIcon className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Liste des ProcessContainers */}
      { expandedView && 
        <div className="p-4 overflow-x-auto">
          <div className="flex flex-row gap-4 w-max min-w-full pb-8">
            {sortedProcessContainers.map(container => {
              const containerTasks = tasks[container.id] || []
              return (
                <ProcessCard
                  key={container.id}
                  group={{
                    codageInterne: bienLabel,
                    processType: container.name,
                    tasks: containerTasks,
                    container: container,
                    taskSource: 'process'                    
                  }}
                  onComplete={onComplete}
                  onStart={onStart}
                  onValidate={onValidate}
                  onReopen={onReopen}
                />
              )
            })}
          </div>
        </div>
      }

      {/* Modal confirmation annulation */}
      {showCancelModal && (
        <dialog open className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">Archiver le macro process</h3>
            <p className="py-4">
              Êtes-vous sûr de vouloir archiver le macro process <strong>{bienLabel} – {purposeLabel}</strong> ?
              <br />
              <span className="text-sm text-gray-500">Les containers en attente seront annulés.</span>
            </p>
            <div className="modal-action">
              <button
                onClick={handleConfirmCancel}
                disabled={isActing}
                className="btn btn-error"
              >
                {isActing ? <span className="loading loading-spinner loading-xs" /> : 'Confirmer'}
              </button>
              <button onClick={() => setShowCancelModal(false)} disabled={isActing} className="btn btn-outline">
                Retour
              </button>
            </div>
          </div>
          <div className="modal-backdrop bg-black/50" onClick={() => !isActing && setShowCancelModal(false)} />
        </dialog>
      )}

      {/* Modal confirmation suppression (admin) */}
      {showDeleteModal && (
        <dialog open className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg text-error">Supprimer le macro process</h3>
            <p className="py-4">
              Cette action est <strong>irréversible</strong>. Êtes-vous sûr de vouloir supprimer{' '}
              <strong>{bienLabel} – {purposeLabel}</strong> ?
            </p>
            <div className="modal-action">
              <button
                onClick={handleConfirmDelete}
                disabled={isActing}
                className="btn btn-error"
              >
                {isActing ? <span className="loading loading-spinner loading-xs" /> : 'Supprimer définitivement'}
              </button>
              <button onClick={() => setShowDeleteModal(false)} disabled={isActing} className="btn btn-outline">
                Retour
              </button>
            </div>
          </div>
          <div className="modal-backdrop bg-black/50" onClick={() => !isActing && setShowDeleteModal(false)} />
        </dialog>
      )}
    </div>
  )
}

export default MacroProcessSection
